'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Clock } from 'lucide-react';
import { Article } from '@/types/content';
import { tagService } from '@/lib/tag-service';
import { Tag } from '@/types/tags';
import { getArticlePath } from '@/lib/article-url-utils';

interface HomepageArticleCardProps {
  article: Article;
  priority?: boolean;
}

export default function HomepageArticleCard({ article, priority = false }: HomepageArticleCardProps) {
  const [primaryTag, setPrimaryTag] = useState<Tag | null>(null);

  useEffect(() => {
    async function fetchTag() {
      if (article.tags?.[0]) {
        try {
          const tag = await tagService.getTag(article.tags[0]);
          if (tag?.isActive) setPrimaryTag(tag);
        } catch {}
      }
    }
    fetchTag();
  }, [article.tags]);

  const articleUrl = getArticlePath(article);
  const tagName = primaryTag?.name || 'Article';
  const tagColor = primaryTag?.color;

  return (
    <Link
      href={articleUrl}
      className="group relative block w-full h-[220px] sm:h-[280px] overflow-hidden"
    >
      {/* Background image */}
      {article.featuredImage ? (
        <Image
          src={article.featuredImage}
          alt={article.title}
          fill
          className="object-cover transition-transform duration-700 group-hover:scale-105"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
          priority={priority}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-200 via-gray-300 to-gray-400 dark:from-surface2 dark:via-surface dark:to-ink" />
      )}

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />

      {/* Top: Tag badge */}
      <div className="absolute top-3 left-3">
        <span
          className="inline-flex items-center px-2 py-0.5 text-mono-sm font-bold uppercase tracking-widest"
          style={{
            backgroundColor: tagColor || '#E8A832',
            color: '#0D0D0D',
          }}
        >
          {tagName}
        </span>
      </div>

      {/* Bottom: Title + meta */}
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <h3 className="font-display text-base font-bold text-parchment leading-snug line-clamp-2 mb-2 group-hover:text-scout-gold transition-colors">
          {article.title}
        </h3>
        <div className="flex items-center gap-3 font-mono text-mono-sm text-parchment-dim">
          {article.publishedAt && (
            <span>
              {article.publishedAt.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}
            </span>
          )}
          {article.readTime && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {article.readTime} min
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
